import { fetchCoupon } from 'admin/actions/coupon';
import ApiResults from 'admin/components/molecules/ApiResults';
import BreadCrumb, { CrumbType } from 'admin/components/molecules/BreadCrumb';
import useCoupon, { CouponParams } from 'admin/hooks/useCoupon';
import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import EditCoupon from './EditCoupon';

type Coupon = Awaited<ReturnType<typeof fetchCoupon>>;

type RouteParams = {
  id: string;
};

type CouponDetailProps = RouteComponentProps<RouteParams>;

type ApiResultType = {
  text: string;
  isError: boolean;
} | null;

const PageWrapper = styled.div`
  margin-bottom: 50px;
  ${ApiResults} {
    margin-top: 10px;
  }
`;

const generateCrumbs = (id: string): CrumbType[] => {
  return [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: '販促管理', href: '', isCurrentPage: true },
    { value: 'クーポン管理', href: '/promotion/coupons', isCurrentPage: false },
    { value: id === 'create' ? 'クーポン新規作成' : 'クーポン詳細', href: '', isCurrentPage: true },
  ];
};

const CouponDetail = ({ match }: CouponDetailProps) => {
  const couponId = match.params.id;
  const { coupon, submitUpdate, submitCreate, error } = useCoupon(couponId);
  const [apiResult, setApiResult] = useState<ApiResultType>(null);

  const crumbs = useMemo(() => generateCrumbs(couponId), [couponId]);

  const onSubmit = (params: Coupon) => {
    if (couponId !== 'create') {
      const { id, issued_shop, private_money, available_shops, token, ...bodyParams } = params;

      const submitParams: CouponParams = { ...bodyParams };

      if (submitParams.is_shop_specified && available_shops) {
        submitParams.available_shop_ids = available_shops.map((s) => s.id).join(',');
      }

      setApiResult(null);
      const onSuccess = () => {
        setApiResult({ text: 'クーポンは正常に更新されました', isError: false });
      };

      if (id) {
        submitUpdate(id, submitParams, onSuccess);
      }
    } else {
      const { id, issued_shop, private_money, available_shops, token, ...bodyParams } = params;

      const submitParams: CouponParams = { ...bodyParams };

      if (submitParams.is_shop_specified && available_shops) {
        submitParams.available_shop_ids = available_shops.map((s) => s.id).join(',');
      }

      if (issued_shop) {
        submitParams.issued_shop_id = issued_shop.id;
      }

      if (private_money) {
        submitParams.private_money_id = private_money.id;
      }

      setApiResult(null);
      const onSuccess = () => {
        setApiResult({ text: 'クーポンの作成に成功しました', isError: false });
      };

      submitCreate(submitParams, onSuccess);
    }
  };

  useEffect(() => {
    if (error.message) {
      setApiResult({ text: error.message, isError: true });
    }
  }, [error]);

  return (
    <PageWrapper>
      <BreadCrumb crumbs={crumbs} />
      {apiResult && <ApiResults messages={[apiResult]} />}
      <EditCoupon
        coupon={coupon}
        onSubmit={onSubmit}
        mode={couponId === 'create' ? 'create' : 'edit'}
      />
    </PageWrapper>
  );
};

export default CouponDetail;
